import React, { useMemo } from 'react';
import { useGetPhonicsSoundsQuery } from '@/api/types';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { findSoundsInString } from '@/utils/sounds';
import { Activity } from '../Activity';
import { ClayIntroStep } from '../components/ClayIntroStep';
import { ClaySoundStep } from '../components/ClaySoundStep';
import { ActivityTypeProps } from '../types';
import { getStepNumber } from '../utils';
import { getRandomEntries } from '@/utils/array';

const MAX_SOUNDS = 6;

export const ReadingReviewSounds = ({
  activity,
  mode,
  isPreview,
}: ActivityTypeProps) => {
  const { getLessonByActivity } = useLessonPlayer();

  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const sounds = useMemo(() => {
    if (!activity.sounds) {
      return;
    }

    if (!soundsData) {
      return;
    }

    const lesson = getLessonByActivity(activity);

    const soundsFromString = findSoundsInString(
      soundsData?.getPhonicsSounds.map((sound) => sound.name) || [],
      activity.sounds,
      lesson?.number,
    );

    return getRandomEntries(soundsFromString, MAX_SOUNDS);
  }, [activity, soundsData, getLessonByActivity]);

  const NUMBER_OF_STEPS_PER_SOUND = 2;

  return (
    sounds && (
      <Activity
        steps={[
          <ClayIntroStep
            key={`${activity.id}-title`}
            stepIdentifier={`${activity.id}-0-title`}
            activity={activity}
            mode={mode}
            sounds={sounds}
            totalSteps={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
          />,
          ...sounds.flatMap((sound, index) => [
            <ClaySoundStep
              key={`${activity.id}-${index}-1`}
              stepIdentifier={`${activity.id}-${index}-1`}
              activity={activity}
              mode={mode}
              sound={sound}
              variant="grapheme"
              title="Say the sound"
              stepNumber={getStepNumber(
                index + 1,
                0,
                NUMBER_OF_STEPS_PER_SOUND,
              )}
              totalSteps={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
              isPreview={isPreview}
            />,
            <ClaySoundStep
              key={`${activity.id}-${index}-2`}
              stepIdentifier={`${activity.id}-${index}-2`}
              activity={activity}
              mode={mode}
              sound={sound}
              variant="picture"
              title="See the picture"
              stepNumber={getStepNumber(
                index + 1,
                1,
                NUMBER_OF_STEPS_PER_SOUND,
              )}
              totalSteps={sounds.length * NUMBER_OF_STEPS_PER_SOUND + 1}
              isPreview={isPreview}
            />,
          ]),
        ]}
      />
    )
  );
};
